import * as React from 'react';
import { useCallback } from 'react';
import cn from 'classnames';
import Card from '@mui/material/Card';

import { InfoMessagePositionsEnum, SegmentWithTransferFragment } from '@websky/graphql';
import { MobileSegmentInfoProps } from '../../types';
import * as icons from './icons';
import { useTheme } from '../../../theme';
import { useMobileSegmentFlatInfo } from './hooks';
import AircraftPopup from '../../../AircraftPopup';
import Amenities from '../../../Amenities';
import StopsInfo from '../../../DesktopFlightRoute/components/Segment/StopsInfo/StopsInfo';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../../context';
import AircraftLivery from '../../../AircraftLivery';
import InfoMessages from '../../../InfoMessages';
import RouteInfo from '../../../DesktopFlightRoute/components/Segment/RouteInfo/RouteInfo';
import { CheckinCardItem } from '@shared/CheckinCard';

export enum MobileSegmentType {
	Recommended,
	Selected
}

export interface MobileSegmentRenderers {
	renderCompanyName?: (props: { companyName: string; segment: SegmentWithTransferFragment }) => React.ReactNode;
}

export interface Props extends MobileSegmentRenderers {
	type?: MobileSegmentType;
	showAirplaneInfo?: boolean;
	className?: string;
	stopCount?: number;
	first?: boolean;
	last?: boolean;
	multipleSegments?: boolean;
	totalDuration?: React.ReactNode;
	isReturnFlight?: boolean;
	canceled?: boolean;
	onClickSegment?: () => void;
}

const MobileSegmentFlat: React.FC<MobileSegmentInfoProps & Props> = (props, { type }) => {
	const { MobileSegmentFlat: theme } = useTheme('MobileSegmentInfo');
	const { t } = useTranslation('DesktopFlightInfo');

	const iataCode = useConfig().global.companyInfo.iataCode;
	const hasOperatingCarrier = ['A9', 'RM'].includes(iataCode);

	const { firstSegment, lastSegment } = useMobileSegmentFlatInfo(props);

	const operatingCarrier =
		hasOperatingCarrier && firstSegment?.marketingAirline.iata !== firstSegment?.operatingAirline.iata
			? firstSegment.operatingAirline.name
			: null;

	const companyName = firstSegment?.operatingAirline?.name;
	const companyIcon = firstSegment?.operatingAirline?.icon;

	const renderAircraftButton = useCallback(
		(onClick: () => void) => (
			<AircraftLivery
				className={theme.image}
				returnClassName={theme.image_return}
				isReturnFlight={props.isReturnFlight}
				aircraft={firstSegment?.aircraft}
				onClick={onClick}
			/>
		),
		[firstSegment]
	);

	return (
		<CheckinCardItem>
			<Card elevation={0} className={cn(theme.card, props.className)}>
				{props.renderCompanyName?.({ companyName, segment: props.segments[0] }) ?? (
					<CheckinCardItem className={cn(theme.title, { [theme.canceled]: props.canceled })}>
						<img src={companyIcon} alt={companyName} />
						{companyName}
					</CheckinCardItem>
				)}

				<div
					className={cn(theme.segment, {
						[theme.canceled]: props.canceled
					})}
					onClick={props.onClickSegment}
				>
					<RouteInfo
						segmentWithTransfer={{ segment: { ...firstSegment, arrival: lastSegment.arrival } }}
						{...props}
					/>
				</div>

				<div className={theme.icons}>
					{type === MobileSegmentType.Recommended && icons.recommendedIcon}
					{type === MobileSegmentType.Selected && icons.selectedIcon}
				</div>

				{props.showAirplaneInfo && !props.canceled && (
					<div className={theme.aircraft}>
						<AircraftPopup
							id={firstSegment.aircraft?.id}
							operatingIata={firstSegment.operatingAirline.iata}
							name={firstSegment.aircraft?.name}
							renderButton={renderAircraftButton}
						/>

						<div className={theme.aircraft__details}>
							<div className={theme.aircraft__name}>{firstSegment.aircraft?.name}</div>

							{firstSegment.flightInfo?.amenities?.length > 0 && (
								<Amenities
									className={theme.aircraft__amenities}
									amenities={firstSegment.flightInfo.amenities}
								/>
							)}
						</div>

						{operatingCarrier && (
							<div className={theme.aircraft__operatingCarrier}>
								{t('Operating carrier')}: {operatingCarrier}
							</div>
						)}
					</div>
				)}
				{!props.canceled &&
					props.segments.map(({ segment, transferDuration }) => {
						if (!transferDuration && !segment.stops?.length) {
							return null;
						}

						return (
							<div key={segment.id}>
								<StopsInfo
									stopCityName={segment.arrival.airport.city.name}
									stops={segment.stops}
									transferDuration={transferDuration}
									className={theme.stop}
								/>

								<InfoMessages
									position={InfoMessagePositionsEnum.stopPoint}
									className={theme.stopPointInfoMessage}
									itemHtmlClassName={theme.stopPointInfoMessage__htmlMessage}
									messages={segment.messages}
								/>
							</div>
						);
					})}
				{props.totalDuration && <div className={theme.totalDuration}>{props.totalDuration}</div>}
			</Card>
		</CheckinCardItem>
	);
};

export default MobileSegmentFlat;
